import { Button } from '../ui/button';
import { Calendar, Pencil, Trash2, Clock } from 'lucide-react';
import { assignmentOvertime } from './assignmentUtils';

// Helper to format hours display
const formatHours = (assignment) => {
  // First check for direct hours field
  if (assignment.hours != null && assignment.hours > 0) {
    return `${assignment.hours} hr${assignment.hours !== 1 ? 's' : ''}`;
  }
  // Fallback to calculating from start/end times (legacy data)
  if (assignment.start_time && assignment.end_time) {
    // Calculate hours from times
    try {
      const [sh, sm] = assignment.start_time.split(':').map(Number);
      const [eh, em] = assignment.end_time.split(':').map(Number);
      const hours = Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
      return `${hours.toFixed(1)} hrs`;
    } catch {
      return `${assignment.start_time} - ${assignment.end_time}`;
    }
  }
  return null;
};

export default function AssignmentList({ items, canModify, currentUserTechId, currentUserId, onEdit, onRemove, icon: Icon, nameKey }) {
  if (!items.length) return <p data-testid="no-assignments" className="text-slate-500 text-center py-4">None assigned</p>;

  const grouped = {};
  items.forEach(a => { if (!grouped[a.date]) grouped[a.date] = []; grouped[a.date].push(a); });

  return (
    <div className="space-y-4">
      {Object.keys(grouped).sort().map(date => (
        <div key={date} className="border border-slate-200 rounded-lg" data-testid={`${nameKey.replace(/_/g, '-')}-assignment-day-${date}`}>
          <div className="bg-slate-100 px-4 py-2 flex items-center gap-2">
            <Calendar size={16} className="text-slate-500" />
            <span className="font-semibold text-slate-700" data-testid={`${nameKey.replace(/_/g, '-')}-assignment-date-${date}`}>{new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
          </div>
          <div className="divide-y">
            {grouped[date].map(a => {
              const hoursDisplay = formatHours(a);
              const overtime = assignmentOvertime(a);
              // Allow edit/delete if canModify (manager/foreman) OR if this is the technician's own assignment (by tech_id for time or by created_by for equipment).
              const isOwnByTech = currentUserTechId && a.tech_id === currentUserTechId;
              const isOwnByCreator = currentUserId && a.created_by === currentUserId;
              const showActions = canModify || isOwnByTech || isOwnByCreator;
              
              return (
                <div key={a.id} className="px-4 py-3 flex items-center justify-between gap-2" data-testid={`assignment-${a.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center shrink-0"><Icon size={16} className="text-slate-600" /></div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 break-words" data-testid={`assignment-name-${a.id}`}>{a[nameKey]}</p>
                      {(hoursDisplay || overtime > 0) && (
                        <p className="text-xs text-slate-500 flex flex-wrap items-center gap-1" data-testid={`assignment-time-${a.id}`}>
                          <Clock size={12} />
                          {hoursDisplay && <span data-testid={`assignment-hours-${a.id}`}>{hoursDisplay}</span>}
                          {overtime > 0 && (
                            <span className={`${hoursDisplay ? 'ml-2' : ''} text-amber-700 font-semibold`} data-testid={`overtime-${a.id}`}>
                              {hoursDisplay ? '+' : ''}{overtime} OT
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  {showActions && (
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" aria-label={`Edit assignment for ${a[nameKey]} on ${a.date}`} onClick={() => onEdit(a)} data-testid={`edit-assignment-${a.id}`}><Pencil size={14} /></Button>
                      <Button size="sm" variant="outline" aria-label={`Delete assignment for ${a[nameKey]} on ${a.date}`} onClick={() => onRemove(a.id)} className="text-red-600" data-testid={`delete-assignment-${a.id}`}><Trash2 size={14} /></Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
